import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import type {
  HubspotContactFieldMapping,
  HubspotContactProperty,
  HubspotLeadAttribute,
  HubspotLeadAttributeValue,
} from "./types";

// Section headers for the attribute dropdown. The catalog itself comes from the
// backend (`crmSelfConfig.getHubspotLeadAttributes` → `HUBSPOT_LEAD_ATTRIBUTES`),
// the same constant the sync reads — so the dropdown and the runtime cannot drift.
// Only the Spanish header copy lives here; `group` has no runtime meaning.
const GROUP_LABELS: Record<HubspotLeadAttribute["group"], string> = {
  identidad: "Identidad",
  atribucion: "Atribución",
  preferencias: "Preferencias del lead",
};

const GROUP_ORDER: Array<HubspotLeadAttribute["group"]> = [
  "identidad",
  "atribucion",
  "preferencias",
];

// The three shapes `buildHubspotContactProperties` resolves, in its own order of
// precedence. "passthrough" is stored as *neither* field set — it used to be
// reachable only by picking "constante" and leaving the box empty, which no
// merchant would ever discover. It is the default, so it gets its own radio.
type MappingMode = "passthrough" | "constant" | "valueMap";

const MODE_LABELS: Record<MappingMode, string> = {
  passthrough: "Copiar tal cual",
  constant: "Valor fijo",
  valueMap: "Traducir valores",
};

const MODE_HINTS: Record<MappingMode, string> = {
  passthrough: "Se escribe el dato del lead sin cambios. Es lo más común.",
  constant:
    "Se escribe siempre el mismo texto, sin importar lo que diga el lead. Sirve para marcar el origen, por ejemplo Canal = Whatsapp.",
  valueMap:
    "Úsalo cuando en HubSpot manejas otros nombres para los mismos valores. Lo que no esté en la tabla no se escribe.",
};

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

const resolveMode = (mapping: HubspotContactFieldMapping): MappingMode => {
  if (mapping.valueMap) return "valueMap";
  if (mapping.constantValue !== undefined) return "constant";
  return "passthrough";
};

export type HubspotMappedObject = "contact" | "deal";

const OBJECT_COPY: Record<HubspotMappedObject, { title: string; target: string; record: string }> =
  {
    contact: {
      title: "Mapeo de campos de contacto",
      target: "cada propiedad del contacto",
      record: "un contacto recién creado",
    },
    deal: {
      title: "Mapeo de campos del negocio",
      target: "cada propiedad del negocio (deal)",
      record: "un negocio recién creado",
    },
  };

type HubspotContactFieldsEditorProps = {
  object?: HubspotMappedObject;
  mappings: HubspotContactFieldMapping[];
  onChange: (mappings: HubspotContactFieldMapping[]) => void;
  properties: HubspotContactProperty[];
  isLoadingProperties: boolean;
  leadAttributes: HubspotLeadAttribute[];
};

const HubspotContactFieldsEditor = ({
  object = "contact",
  mappings,
  onChange,
  properties,
  isLoadingProperties,
  leadAttributes,
}: HubspotContactFieldsEditorProps): JSX.Element => {
  const copy = OBJECT_COPY[object];
  const propertyByName = (name: string) => properties.find((p) => p.name === name);
  const attributeByKey = (key: string) => leadAttributes.find((a) => a.key === key);

  // ~27 attributes is well past the point where a flat <select> reads well, so
  // they are grouped into <optgroup>s (the Bitrix #1773 restructure's lesson).
  const attributesByGroup = GROUP_ORDER.map((group) => ({
    group,
    attributes: leadAttributes.filter((attribute) => attribute.group === group),
  })).filter((section) => section.attributes.length > 0);

  const updateMapping = (index: number, patch: Partial<HubspotContactFieldMapping>) => {
    onChange(mappings.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const handleAddRow = () => {
    // Default to whatever the catalog leads with, never a hardcoded key that could
    // one day not be in it.
    onChange([...mappings, { leadAttr: leadAttributes[0]?.key ?? "", hubspotProperty: "" }]);
  };

  const handleRemoveRow = (index: number) => {
    resetCustomEntries();
    onChange(mappings.filter((_, i) => i !== index));
  };

  const handleModeChange = (index: number, mode: MappingMode) => {
    resetCustomEntries();
    if (mode === "passthrough") {
      updateMapping(index, { constantValue: undefined, valueMap: undefined });
      return;
    }
    if (mode === "constant") {
      updateMapping(index, { valueMap: undefined, constantValue: "" });
      return;
    }
    updateMapping(index, { constantValue: undefined, valueMap: {} });
  };

  // Entries are addressed by POSITION, not by key. Rewriting the record in place
  // keeps a row where the merchant put it: the previous `delete old; set new`
  // moved the entry to the end of the object on the first keystroke, so a row
  // being edited jumped to the bottom of the list.
  const replaceValueMapEntry = (
    index: number,
    entryIndex: number,
    entry: [string, string]
  ) => {
    const entries = Object.entries(mappings[index].valueMap || {});
    const next = entries.map((current, i) => (i === entryIndex ? entry : current));
    updateMapping(index, { valueMap: Object.fromEntries(next) });
  };

  const handleLeadValueChange = (index: number, entryIndex: number, leadValue: string) => {
    const entries = Object.entries(mappings[index].valueMap || {});
    replaceValueMapEntry(index, entryIndex, [leadValue, entries[entryIndex]?.[1] ?? ""]);
  };

  const handleHubspotValueChange = (index: number, entryIndex: number, hubspotValue: string) => {
    const entries = Object.entries(mappings[index].valueMap || {});
    replaceValueMapEntry(index, entryIndex, [entries[entryIndex]?.[0] ?? "", hubspotValue]);
  };

  const handleAddValueMapEntry = (index: number) => {
    const current = { ...(mappings[index].valueMap || {}) };
    // Pre-select the first value the merchant has not mapped yet. With a closed
    // list this is almost always the one they wanted, and it avoids the blank-key
    // collision below: two entries keyed "" would silently collapse into one.
    const attribute = attributeByKey(mappings[index].leadAttr);
    const nextValue =
      attribute?.knownValues?.find((known) => !(known.value in current))?.value ?? "";
    if (nextValue in current) return;
    current[nextValue] = "";
    updateMapping(index, { valueMap: current });
  };

  const handleRemoveValueMapEntry = (index: number, entryIndex: number) => {
    resetCustomEntries();
    const entries = Object.entries(mappings[index].valueMap || {});
    updateMapping(index, {
      valueMap: Object.fromEntries(entries.filter((_, i) => i !== entryIndex)),
    });
  };

  // Rows the merchant explicitly switched to free text via "Otro valor…". A row
  // whose stored value simply is not in the list renders as free text too (see
  // `isCustomEntry`), so this only has to remember the choice for an EMPTY value —
  // which is indistinguishable from "nothing picked yet" without it.
  const [customEntries, setCustomEntries] = useState<Set<string>>(new Set());

  const entryId = (index: number, entryIndex: number) => `${index}:${entryIndex}`;

  // Keys are positional, so anything that renumbers rows or entries invalidates the
  // whole set — leaving it would put a DIFFERENT row into free-text mode. The only
  // thing lost is an "Otro valor…" click whose box is still empty.
  const resetCustomEntries = () => setCustomEntries(new Set());

  const setCustomEntry = (index: number, entryIndex: number, isCustom: boolean) => {
    setCustomEntries((current) => {
      const next = new Set(current);
      if (isCustom) next.add(entryId(index, entryIndex));
      else next.delete(entryId(index, entryIndex));
      return next;
    });
  };

  // Sentinel for the "type it myself" option. A `__`-prefixed value can never
  // collide with a real one: every lead value Palomma stores is a portal code, a
  // category name or an enum member.
  const CUSTOM_VALUE_OPTION = "__custom__";

  const knownValueText = (known: HubspotLeadAttributeValue) =>
    known.label === known.value ? known.value : `${known.label} (${known.value})`;

  /**
   * The LEFT column of a value-map row: the value as Palomma stores it.
   *
   * A picking list wherever we can name the possibilities — the merchant cannot
   * guess that "Tipo de inmueble" is `house`, and `buildHubspotContactProperties`
   * skips an unmatched key in silence, so a typo here produces no error and no
   * written property. Free text stays reachable for every attribute: the catalog's
   * list is complete for the fixed enums but only a snapshot for the merchant's own
   * vocabulary, and refusing a value outside it would make a real mapping
   * unexpressible.
   */
  const renderLeadValueControl = (
    index: number,
    entryIndex: number,
    attribute: HubspotLeadAttribute | undefined,
    leadValue: string,
    usedValues: string[]
  ): JSX.Element => {
    const knownValues = attribute?.knownValues;
    const isKnown = !!knownValues?.some((known) => known.value === leadValue);
    // A stored value outside the list renders as free text on its own — that is how
    // a mapping made before the list existed, or against a renamed category, stays
    // editable instead of silently snapping to another value.
    const isCustom =
      !knownValues ||
      (!isKnown && (!!leadValue || customEntries.has(entryId(index, entryIndex))));

    if (isCustom) {
      return (
        <>
          <Input
            value={leadValue}
            onChange={(e) => handleLeadValueChange(index, entryIndex, e.target.value)}
            placeholder="Valor del lead (ej: house)"
            className="bg-white"
          />
          {knownValues && (
            <div className="flex flex-col gap-0.5">
              {!!leadValue && attribute?.valuesAreExhaustive && (
                <p className="text-xs text-orange-600">
                  "{leadValue}" no está entre los valores de {attribute.label}. Si el lead nunca
                  lo trae, esta fila no escribe nada.
                </p>
              )}
              <button
                type="button"
                className="w-fit text-xs text-blue-600 underline"
                onClick={() => {
                  setCustomEntry(index, entryIndex, false);
                  handleLeadValueChange(index, entryIndex, "");
                }}
              >
                Elegir de la lista
              </button>
            </div>
          )}
        </>
      );
    }

    return (
      <select
        className={selectClass}
        value={leadValue}
        onChange={(e) => {
          if (e.target.value === CUSTOM_VALUE_OPTION) {
            setCustomEntry(index, entryIndex, true);
            handleLeadValueChange(index, entryIndex, "");
            return;
          }
          setCustomEntry(index, entryIndex, false);
          handleLeadValueChange(index, entryIndex, e.target.value);
        }}
      >
        <option value="">Selecciona un valor del lead</option>
        {knownValues.map((known) => (
          <option
            key={known.value}
            value={known.value}
            // Mapping the same lead value twice collapses the two rows into one,
            // because the value map is keyed by it.
            disabled={known.value !== leadValue && usedValues.includes(known.value)}
          >
            {knownValueText(known)}
          </option>
        ))}
        <option value={CUSTOM_VALUE_OPTION}>Otro valor…</option>
      </select>
    );
  };

  const renderOptionValueControl = (
    property: HubspotContactProperty | undefined,
    value: string,
    onValueChange: (v: string) => void,
    placeholder: string
  ) => {
    if (property && property.options.length > 0) {
      return (
        <select className={selectClass} value={value} onChange={(e) => onValueChange(e.target.value)}>
          <option value="">Selecciona un valor</option>
          {property.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label} ({option.value})
            </option>
          ))}
        </select>
      );
    }
    return (
      <Input
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder={placeholder}
        className="bg-gray-50"
      />
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <p className="text-xs font-semibold text-gray-600">{copy.title}</p>
        <p className="text-xs text-gray-500">
          Decide qué información del lead se guarda en {copy.target} en HubSpot. Cada fila es una
          regla: eliges el dato del lead, la propiedad de HubSpot donde escribirlo y cómo enviarlo.
        </p>
        <p className="text-xs text-gray-500">
          Si el lead todavía no tiene ese dato, la propiedad no se toca — nunca la sobrescribimos con
          un valor vacío. Las preferencias se van llenando solas a medida que el lead conversa, así
          que es normal que {copy.record} llegue sin ellas.
        </p>
      </div>

      {isLoadingProperties ? (
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <p className="text-xs text-gray-500">Cargando propiedades de HubSpot...</p>
        </div>
      ) : (
        <>
          {mappings.length === 0 && (
            <p className="rounded-md border border-dashed border-gray-300 p-3 text-xs text-gray-500">
              Todavía no hay campos mapeados. Agrega el primero para empezar a enviar información del
              lead a HubSpot.
            </p>
          )}

          {mappings.map((mapping, index) => {
            const property = propertyByName(mapping.hubspotProperty);
            const isUnavailableProperty =
              !!mapping.hubspotProperty && !property && properties.length > 0;
            const attribute = attributeByKey(mapping.leadAttr);
            const mode = resolveMode(mapping);
            const valueMapEntries = Object.entries(mapping.valueMap || {});
            const usedLeadValues = valueMapEntries.map(([ourValue]) => ourValue);
            // "Agregar valor" would otherwise be a no-op click: a second entry keyed
            // "" collapses into the first, and every known value may already be taken.
            const canAddValue =
              (attribute?.knownValues ?? []).some((known) => !usedLeadValues.includes(known.value)) ||
              !usedLeadValues.includes("");
            // Several rows onto one property is a deliberate FALLBACK CHAIN, not a
            // mistake: rows apply in order, a row that resolves overwrites, and a row
            // that skips leaves the previous value standing. That is how "el portal,
            // salvo que sea pauta" is expressed. Order is therefore load-bearing and
            // otherwise invisible, so each row says where it sits in the chain.
            const chain = mapping.hubspotProperty
              ? mappings.filter((m) => m.hubspotProperty === mapping.hubspotProperty)
              : [];
            const chainPosition = mapping.hubspotProperty
              ? mappings
                  .slice(0, index + 1)
                  .filter((m) => m.hubspotProperty === mapping.hubspotProperty).length
              : 0;
            const isChained = chain.length > 1;
            const isLastInChain = chainPosition === chain.length;

            return (
              <div
                key={index}
                className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="grid flex-1 grid-cols-1 gap-2 md:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs">Atributo del lead</Label>
                      <select
                        className={selectClass}
                        value={mapping.leadAttr}
                        onChange={(e) => {
                          // The stored valueMap keys belong to the OLD attribute's
                          // vocabulary; they survive on purpose (so a mis-click is
                          // recoverable) and render as free text with a warning.
                          resetCustomEntries();
                          updateMapping(index, { leadAttr: e.target.value });
                        }}
                      >
                        {attributesByGroup.map((section) => (
                          <optgroup key={section.group} label={GROUP_LABELS[section.group]}>
                            {section.attributes.map((attr) => (
                              <option key={attr.key} value={attr.key}>
                                {attr.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      {attribute?.hint && <p className="text-xs text-gray-500">{attribute.hint}</p>}
                      {attribute?.multiValue && (
                        <p className="text-xs text-gray-500">
                          Puede traer varios valores. Se envían juntos separados por punto y coma,
                          que es lo que HubSpot espera en propiedades de casillas múltiples.
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col gap-1">
                      <Label className="text-xs">Propiedad de HubSpot</Label>
                      <select
                        className={selectClass}
                        value={mapping.hubspotProperty}
                        onChange={(e) => updateMapping(index, { hubspotProperty: e.target.value })}
                      >
                        <option value="">Selecciona una propiedad</option>
                        {isUnavailableProperty && (
                          <option value={mapping.hubspotProperty}>
                            {mapping.hubspotProperty} (no disponible)
                          </option>
                        )}
                        {properties.map((prop) => (
                          <option key={prop.name} value={prop.name}>
                            {prop.label} ({prop.name})
                          </option>
                        ))}
                      </select>
                      {isUnavailableProperty && (
                        <p className="text-xs text-orange-600">
                          Esta propiedad ya no existe en HubSpot o no se puede escribir (es de solo
                          lectura, calculada u oculta). Elige otra o elimina la fila.
                        </p>
                      )}
                      {!mapping.hubspotProperty && (
                        <p className="text-xs text-gray-500">
                          Sin propiedad seleccionada esta fila no hace nada.
                        </p>
                      )}
                      {isChained && (
                        <p className="text-xs text-gray-500">
                          Fila {chainPosition} de {chain.length} hacia esta propiedad.{" "}
                          {isLastInChain
                            ? "Es la última: si encuentra valor, es el que se escribe."
                            : "Si una fila de más abajo encuentra valor, gana esa; si no, queda el de esta."}
                        </p>
                      )}
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6 text-red-600 hover:text-red-700"
                    aria-label="Eliminar campo"
                    onClick={() => handleRemoveRow(index)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Cómo enviar el valor</Label>
                    <div className="flex flex-wrap gap-4">
                      {(Object.keys(MODE_LABELS) as MappingMode[]).map((option) => (
                        <label key={option} className="flex items-center gap-1 text-xs">
                          <input
                            type="radio"
                            name={`mapping-mode-${index}`}
                            checked={mode === option}
                            onChange={() => handleModeChange(index, option)}
                          />
                          {MODE_LABELS[option]}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">{MODE_HINTS[mode]}</p>
                  </div>

                  {mode === "constant" && (
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs">Valor a estampar</Label>
                      {renderOptionValueControl(
                        property,
                        mapping.constantValue || "",
                        (v) => updateMapping(index, { constantValue: v }),
                        "Ej: Whatsapp"
                      )}
                      {!mapping.constantValue && (
                        <p className="text-xs text-orange-600">
                          Sin un valor aquí, se copia el dato del lead tal cual. Escribe el texto fijo
                          o cambia a "Copiar tal cual".
                        </p>
                      )}
                    </div>
                  )}

                  {mode === "valueMap" && (
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-0.5">
                        <Label className="text-xs">
                          Traduce el valor del lead → valor de HubSpot
                        </Label>
                        <p className="text-xs text-gray-500">
                          A la izquierda va el valor tal como lo guarda Palomma; a la derecha, el que
                          quieres ver en HubSpot.
                          {attribute?.multiValue && " Cada valor se traduce por separado."}
                        </p>
                        {attribute?.knownValues && (
                          <p className="text-xs text-gray-500">
                            {attribute.valuesAreExhaustive
                              ? `Estos son todos los valores que ${attribute.label} puede tomar.`
                              : `Estos son los valores de ${attribute.label} según tu configuración; si manejas otros, elige "Otro valor…".`}
                          </p>
                        )}
                      </div>

                      {valueMapEntries.length === 0 && (
                        <p className="text-xs text-gray-500">
                          Sin traducciones, no se escribe nada en esta propiedad. Agrega al menos
                          una.
                        </p>
                      )}

                      {valueMapEntries.map(([ourValue, theirValue], entryIndex) => (
                        <div key={entryIndex} className="flex items-start gap-2">
                          <div className="flex min-w-0 flex-1 flex-col gap-1">
                            {renderLeadValueControl(
                              index,
                              entryIndex,
                              attribute,
                              ourValue,
                              usedLeadValues
                            )}
                          </div>
                          <span className="pt-2.5 text-xs text-gray-400">→</span>
                          <div className="flex min-w-0 flex-1 flex-col gap-1">
                            {renderOptionValueControl(
                              property,
                              theirValue,
                              (v) => handleHubspotValueChange(index, entryIndex, v),
                              "Valor HubSpot (ej: Casa)"
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="mt-2 size-6 text-red-600 hover:text-red-700"
                            aria-label="Eliminar valor"
                            onClick={() => handleRemoveValueMapEntry(index, entryIndex)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-fit"
                          disabled={!canAddValue}
                          onClick={() => handleAddValueMapEntry(index)}
                        >
                          <Plus className="size-4" /> Agregar valor
                        </Button>
                        {!canAddValue && (
                          <p className="text-xs text-gray-500">
                            {attribute?.knownValues
                              ? `Ya mapeaste todos los valores de ${attribute.label}.`
                              : "Completa la fila vacía antes de agregar otra."}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <Button variant="outline" size="sm" className="w-fit" onClick={handleAddRow}>
            <Plus className="size-4" /> Agregar campo
          </Button>
        </>
      )}
    </div>
  );
};

export default HubspotContactFieldsEditor;
